import {
  GroupTextField,
  GroupTextFieldRow,
} from "@elaniintc/goes-design-system";
import {
  DateGetOperations,
  DateStringOperations,
  OperationOptions,
} from "./inputs/OperationSelector";
import { StringOperationInput } from "./inputs/StringOperationInput";
import { NumberOperationInput } from "./inputs/NumberOperationInput";
import { DateOperationInput } from "./inputs/DateOperationInput";
import { BooleanOperationInput } from "./inputs/BooleanOperationInput";
import {
  ArrayFunctionSelector,
  ArrayOperationInput,
} from "./inputs/ArrayOperationInput";
import { DynamicElements } from "./inputs/DynamicElements";
import {
  dateGetFormatOptions,
  dateToStringFormatOptions,
} from "./utils/constants";
import { nodeVariableToGroups } from "../../types";
import {
  DropdownList,
  type DropdownListGroup,
  type IDropdownListItem,
} from "@/components/ui/dropdown-list/dropdown-list";
import { useMemo, useState, useEffect } from "react";
import { useCalculatedFieldContext } from "./context";

export const CalculatedFieldOperation = () => {
  const {
    variables,
    selectedVariable,
    selectedOperation,
    elements,
    setElements,
    selectedDateGetFormat,
    setSelectedDateGetFormat,
    selectedDateToStringFormat,
    setSelectedDateToStringFormat,
    selectedArrayCondition,
    setSelectedArrayCondition,
    selectedArrayFunction,
    setSelectedArrayFunction,
    selectedVariableType,
    selectedVariableMetaType,
    addElement,
    handleVariableChange,
    removeElement,
    updateElement,
    showDynamicElements,
    hasMultipleValues,
    setSelectedOperation,
  } = useCalculatedFieldContext();

  const getHelperText = () => {
    if (!selectedOperation) return "";
    return selectedOperation.helperText || "";
  };

  // Función para cambiar tipo de elemento
  const handleToggleElementType = (id: string) => {
    const newType =
      elements.find((el) => el.id === id)?.type === "inp" ? "var" : "inp";
    setElements(
      elements.map((el) =>
        el.id === id ? { ...el, type: newType, value: "" } : el,
      ),
    );
  };

  // Wrappers para manejar la compatibilidad de tipos
  const handleStringElementsChange = (
    elements: Array<{ id?: string; value?: string }>,
  ) => {
    const convertedElements = elements.map((el) => ({
      id: el.id || Math.random().toString(36).substr(2, 9),
      type: "inp" as const,
      value: el.value || "",
    }));
    setElements(convertedElements);
  };

  const handleArrayElementsChange = (
    elements: Array<{ id?: string; value?: string }>,
  ) => {
    const convertedElements = elements.map((el) => ({
      id: el.id || Math.random().toString(36).substr(2, 9),
      type: "inp" as const,
      value: el.value || "",
    }));
    setElements(convertedElements);
  };

  const [variableSearch, setVariableSearch] = useState("");
  const variableGroups = useMemo(() => {
    const groups = nodeVariableToGroups(variables);
    if (variableSearch) {
      return groups
        .map((group) => ({
          ...group,
          items: group.items.filter(
            (item) =>
              (item.label as string)
                .toLowerCase()
                .includes(variableSearch.toLowerCase()) ||
              group.groupLabels.some((label) =>
                label.toLowerCase().includes(variableSearch.toLowerCase()),
              ),
          ),
        }))
        .filter((group) => group.items.length > 0);
    }
    return groups;
  }, [variables, variableSearch]);

  const [_selectedVariable, setSelectedVariable] =
    useState<IDropdownListItem>();
  const [_selectedGroup, setSelectedGroup] = useState<DropdownListGroup>();

  // Sincronizar la variable seleccionada del contexto con el estado local del DropdownList
  useEffect(() => {
    if (selectedVariable && variableGroups.length > 0) {
      let found = false;
      for (const group of variableGroups) {
        const variable = group.items.find(
          (item) => item.value === selectedVariable,
        );
        if (variable) {
          setSelectedVariable(variable);
          setSelectedGroup(group);
          found = true;
          break;
        }
      }
      // Si no se encuentra la variable, limpiar selección visual
      if (!found) {
        setSelectedVariable(undefined);
        setSelectedGroup(undefined);
      }
    } else {
      // Si no hay variable seleccionada o no hay grupos, limpiar selección visual
      setSelectedVariable(undefined);
      setSelectedGroup(undefined);
    }
  }, [selectedVariable, variableGroups]);

  const onVariableSelection = (variableId: string, groupId: string) => {
    const group = variableGroups.find((g) => g.groupId === groupId);
    const variable = group?.items.find((item) => item.value === variableId);
    setSelectedVariable(variable);
    setSelectedGroup(group);
    handleVariableChange(variableId);
    setSelectedDateToStringFormat("");
    setSelectedDateGetFormat("");
    setElements([]);
    setSelectedArrayCondition("");
  };

  return (
    <div className="p-6 space-y-4">
      <GroupTextField label="Operación" helperText={getHelperText()} required>
        <GroupTextFieldRow>
          <DropdownList
            options={variableGroups}
            selectedValue={_selectedVariable}
            selectedGroup={_selectedGroup}
            toggleSelection={(variable, group) => {
              onVariableSelection(variable.value, group?.groupId || "");
            }}
            widthClassName="flex-1"
            placeholder="Elegir variable"
            search={{
              placeholder: "Buscar variable",
              onSearch: (query) => {
                setVariableSearch(query);
              },
              query: variableSearch,
            }}
          />
          <OperationOptions
            selectedOperation={selectedOperation}
            selectedVariableType={selectedVariableType}
            selectedVariableMetaType={selectedVariableMetaType}
            onOperationChange={(operation) => {
              setSelectedOperation(operation);
              setSelectedDateToStringFormat("");
              setSelectedDateGetFormat("");
              setElements([]);
              setSelectedArrayCondition("");
              if (operation.hasMultipleValues) {
                addElement();
              }
            }}
          />
          {selectedVariableType === "date" &&
            selectedOperation?.name === "date_get_format" && (
              <DateGetOperations
                selectedDateGetFormat={selectedDateGetFormat}
                onDateGetFormatChange={setSelectedDateGetFormat}
                dateGetFormatOptions={dateGetFormatOptions}
              />
            )}
        </GroupTextFieldRow>

        {/* Date to string operations */}
        {selectedVariableType === "date" &&
          selectedOperation?.name === "date_to_string" && (
            <GroupTextFieldRow>
              <DateStringOperations
                selectedDateToStringFormat={selectedDateToStringFormat}
                onDateToStringFormatChange={setSelectedDateToStringFormat}
                dateToStringFormatOptions={dateToStringFormatOptions}
              />
            </GroupTextFieldRow>
          )}
        {/* Array operation inputs */}
        {showDynamicElements &&
          selectedVariableType === "array" &&
          selectedOperation && (
            <GroupTextFieldRow>
              <ArrayOperationInput
                operation={selectedOperation.name}
                elements={elements}
                selectedCondition={selectedArrayCondition}
                onElementsChange={handleArrayElementsChange}
                onConditionChange={setSelectedArrayCondition}
              />
            </GroupTextFieldRow>
          )}

        {/* Array operation inputs */}
        {showDynamicElements &&
          selectedVariableType === "array" &&
          selectedOperation &&
          selectedOperation.name === "map" &&
          selectedArrayCondition && (
            <GroupTextFieldRow>
              <ArrayFunctionSelector
                elements={elements}
                selectedCondition={selectedArrayFunction}
                onConditionChange={setSelectedArrayFunction}
                onElementsChange={handleArrayElementsChange}
              />
            </GroupTextFieldRow>
          )}

        {/* String operation inputs */}
        {showDynamicElements &&
          selectedVariableType === "string" &&
          selectedOperation &&
          selectedOperation.needsValue && (
            <GroupTextFieldRow>
              <StringOperationInput
                operation={selectedOperation.name}
                elements={elements}
                onElementsChange={handleStringElementsChange}
              />
            </GroupTextFieldRow>
          )}

        {/* Number operation inputs */}
        {showDynamicElements &&
          selectedVariableType === "number" &&
          selectedOperation?.needsValue && (
            <GroupTextFieldRow>
              <NumberOperationInput
                operation={selectedOperation.name}
                elements={elements}
                onElementsChange={handleStringElementsChange}
              />
            </GroupTextFieldRow>
          )}

        {/* Date operation inputs */}
        {showDynamicElements &&
          selectedVariableType === "date" &&
          selectedOperation &&
          !["date_get_format", "date_to_string"].includes(
            selectedOperation?.name || "",
          ) && (
            <GroupTextFieldRow>
              <DateOperationInput
                variables={variables}
                operation={selectedOperation.name}
                elements={elements}
                onElementsChange={setElements}
              />
            </GroupTextFieldRow>
          )}

        {/* Boolean operation inputs */}
        {showDynamicElements &&
          selectedVariableType === "boolean" &&
          selectedOperation &&
          selectedOperation.needsValue && (
            <GroupTextFieldRow>
              <BooleanOperationInput
                variables={variables}
                operation={selectedOperation?.name || ""}
                elements={elements}
                onElementsChange={setElements}
              />
            </GroupTextFieldRow>
          )}
      </GroupTextField>

      {/* Dynamic elements for general operations */}
      {hasMultipleValues && (
        <DynamicElements
          variables={variables}
          type={selectedVariableType || "string"}
          elements={elements}
          onUpdateElement={updateElement}
          onRemoveElement={removeElement}
          onAddElement={addElement}
          onToggleElementType={handleToggleElementType}
        />
      )}
    </div>
  );
};
